import '../css/app.css';

import { createApp, h, DefineComponent, Plugin, App } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { PageProps } from '@inertiajs/core';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { ZiggyVue } from 'ziggy-js';
import axios from 'axios';
import { client } from 'laravel-precognition';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import ApplicationMark from '@/Components/ApplicationMark.vue';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import corePagesPaths, { type PagesPath } from '@core/utils/pages-paths';
import blogPagesPaths from '@blog/utils/pages-paths';
import ecommercePagesPaths from '@ecommerce/utils/pages-paths';
import { setDefaultOptions } from 'date-fns';
import { it, de, enGB } from 'date-fns/locale';
import cantineVariantPath, {
    type VariantsPaths,
} from '@cantine/utils/variants-paths';
import coreFormPath, { type FormPaths } from '@core/utils/forms-paths';
import blogFormPath from '@blog/utils/forms-paths';
import ecommerceFormPath from '@ecommerce/utils/forms-paths';
import formsPagesPaths from '@forms/utils/pages-paths';
import formsFormPath from '@forms/utils/forms-paths';
import newsletterPagesPaths from '@newsletter/utils/pages-paths';
import newsletterFormPath from '@newsletter/utils/forms-paths';
import ecommerceDiscountConditionsPaths, {
    DiscountConditionsPaths,
} from '@ecommerce/utils/discount-conditions-paths';

const appName: string = import.meta.env.VITE_APP_NAME || 'Kiboko';

type CreateInnertiaAppProps = Parameters<typeof createInertiaApp>[0];

// higher priority first
const pages: PagesPath[] = [
    [
        import.meta.glob<DefineComponent>("/resources/js/Pages/**/*.vue"),
        (name) => `/resources/js/Pages/${name}.vue`,
    ],
    ...ecommercePagesPaths,
    ...newsletterPagesPaths,
    ...formsPagesPaths,
    ...blogPagesPaths,
    ...corePagesPaths,
];

// lower priority first
const variants: VariantsPaths[] = [
    ...cantineVariantPath,
    [
        import.meta.glob<DefineComponent>(
            '/resources/js/Components/Variants/**/*.vue',
        ),
        '/resources/js/Components/Variants/',
    ],
];

// lower priority first
const discountConditions: DiscountConditionsPaths[] = [
    ...ecommerceDiscountConditionsPaths,
    {
        details: [
            import.meta.glob<DefineComponent>(
                '/resources/js/Components/DiscountConditionsDetails/**/*.vue',
            ),
            '/resources/js/Components/DiscountConditionsDetails/',
        ],
        form: [
            import.meta.glob<DefineComponent>(
                '/resources/js/Components/DiscountConditions/**/*.vue',
            ),
            '/resources/js/Components/DiscountConditions/',
        ],
    },
];

// lower priority first
const forms: FormPaths[] = [
    ...coreFormPath,
    ...blogFormPath,
    ...ecommerceFormPath,
    ...newsletterFormPath,
    ...formsFormPath,
    [
        import.meta.glob<DefineComponent>(
            '/resources/js/Components/Forms/**/*.vue',
        ),
        '/resources/js/Components/Forms/',
    ],
];

export async function prepareApp(
    app: App<Element>,
    props: PageProps,
    plugin: Plugin,
    el: Element,
): Promise<App<Element>> {
    try {
        await i18next.init({
            lng: props.locale,
            interpolation: {
                escapeValue: false,
            },
            fallbackLng: false,
            resources: props.translations,
        });

        axios.defaults.params = {
            ...axios.defaults.params,
            lang: props.locale,
        };

        client.use(axios);

        setDefaultOptions({
            locale:
                {
                    en: enGB,
                    it: it,
                    de: de,
                }[props.locale] ?? enGB,
        });

        app.config.globalProperties.$logos = {
            mark: ApplicationMark,
            logo: ApplicationLogo,
            auth: AuthenticationCardLogo,
        };

        app.config.globalProperties.$forms = {};
        for (const [glob, pathPrefix] of forms) {
            for (const [path, component] of Object.entries(glob)) {
                const name = path
                    .replace(pathPrefix, '')
                    .replace('.vue', '')
                    .replace(/^\//, '');
                app.config.globalProperties.$forms[name] = component;
            }
        }

        app.config.globalProperties.$variants = {};
        for (const [glob, pathPrefix] of variants) {
            for (const [path, component] of Object.entries(glob)) {
                const name = path
                    .replace(pathPrefix, '')
                    .replace('.vue', '')
                    .replace(/^\//, '');
                app.config.globalProperties.$variants[name] = component;
            }
        }

        app.config.globalProperties.$discountConditions = {
            form: {},
            details: {},
        };
        for (const { form, details } of discountConditions) {
            let [glob, pathPrefix] = form;
            for (const [path, component] of Object.entries(glob)) {
                const name = path
                    .replace(pathPrefix, '')
                    .replace('.vue', '')
                    .replace(/^\//, '');
                app.config.globalProperties.$discountConditions.form[name] =
                    component;
            }
            [glob, pathPrefix] = details;
            for (const [path, component] of Object.entries(glob)) {
                const name = path
                    .replace(pathPrefix, '')
                    .replace('.vue', '')
                    .replace(/^\//, '');
                app.config.globalProperties.$discountConditions.details[name] =
                    component;
            }
        }

        app.use(plugin)
            .use(ZiggyVue, {
                ...props.ziggy,
            })
            .use(PrimeVue, {
                theme: 'none',
            })
            .use(I18NextVue, { i18next })
            .use(ConfirmationService)
            .use(ToastService)
            .mount(el);
    } catch (e) {
        console.error("Errore durante l'inizializzazione dell'app:", e);
        el.innerHTML =
            "Si è verificato un errore durante il caricamento dell'applicazione. Riprova più tardi.";
    }

    return app;
}

export async function resolve(name: string): Promise<DefineComponent> {
    for (const path of pages) {
        const [appPages, getPagePath] = path;
        const appPagePath = getPagePath(name);
        if (appPages[appPagePath]) {
            return await resolvePageComponent<DefineComponent>(
                appPagePath,
                appPages,
            );
        }
    }
    throw new Error(
        `Impossibile risolvere la pagina del componente "${name}".`,
    );
}

export function title(title: string): string {
    return `${title} - ${appName}`;
}

createInertiaApp({
    title: title,
    resolve: resolve,
    setup: (async ({
        el,
        App,
        props,
        plugin,
    }: Parameters<CreateInnertiaAppProps['setup']>[0]) => {
        let app = createApp({ render: () => h(App, props) });
        app = await prepareApp(app, props.initialPage.props, plugin, el);
        return app;
    }) as unknown as CreateInnertiaAppProps['setup'],
    progress: {
        color: '#4B5563',
    },
}).catch((e: unknown) => {
    console.error('Error initializing Inertia: ', e);
});
